import React from 'react';
import { useTranslation } from 'react-i18next';
import { Button, Modal, ModalHeader, ModalBody, ModalFooter, FormGroup, Label } from 'reactstrap';
import { Formik, Form, FormikValues } from 'formik';
import isEqual from 'lodash/isEqual';

import { ExpressionModel } from 'src/shared/models/questionnaire.model';
import { expressionSchema } from 'src/shared/schemas/validation.schema';
import TextField from 'src/shared/components/form-inputs/TextField';
import Spinner from 'src/shared/components/Spinner';

interface Props {
  isOpen: boolean;
  toggleModal: () => void;
  confirm: (values: FormikValues) => Promise<void>;
  selectedExpression: ExpressionModel | undefined;
}

const ExpressionsModal: React.FC<Props> = ({
  isOpen,
  toggleModal,
  confirm,
  selectedExpression: selectedAttributeValidator,
}) => {
  const { t } = useTranslation();

  const title = 'expressions.createExpression';
  const submitButtonText = 'common.create';

  const getInitialFormValues = () => ({
    title: selectedAttributeValidator?.title || '',
    expression: selectedAttributeValidator?.expression || '',
  });

  const initialFormValues = getInitialFormValues();

  return (
    <Modal isOpen={isOpen}>
      <ModalHeader>{t(title)}</ModalHeader>
      <Formik
        validateOnChange={false}
        validateOnBlur={false}
        initialValues={initialFormValues}
        validationSchema={expressionSchema}
        onSubmit={async (values, { setSubmitting }) => {
          if (isEqual(values, initialFormValues)) {
            toggleModal();
            return;
          }
          await confirm(values);
          setSubmitting(false);
        }}
      >
        {({ isSubmitting }) => (
          <Form>
            <ModalBody>
              <FormGroup>
                <Label for="title">{t('common.title')}</Label>
                <TextField name="title" />
              </FormGroup>
              <FormGroup>
                <Label for="expression">{t('common.expression')}</Label>
                <TextField name="expression" />
              </FormGroup>
            </ModalBody>

            <ModalFooter>
              <Spinner loading={isSubmitting} />

              <Button type="submit" color="primary" disabled={isSubmitting}>
                {t(submitButtonText)}
              </Button>

              <Button color="secondary" onClick={toggleModal}>
                {t('common.cancel')}
              </Button>
            </ModalFooter>
          </Form>
        )}
      </Formik>
    </Modal>
  );
};

export default ExpressionsModal;
